import React, { useRef, useState } from "react"
import { NodeViewContent, NodeViewWrapper } from "@tiptap/react"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
	IconCopyOutline18 as Copy,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import type { NodeViewProps } from "@tiptap/react"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../../../internal/command"
import {
	InternalPopover,
	InternalPopoverContent,
	InternalPopoverPortal,
	InternalPopoverTrigger,
} from "../../../internal/popover"

interface CodeBlockComponentProps {
	node: {
		attrs: {
			language: string | null
		}
	}
	updateAttributes: (attrs: Record<string, string>) => void // Adjusted to match NodeViewProps
	extension: {
		options: {
			lowlight: {
				listLanguages: () => string[]
			}
			isLanguageEditable: boolean
		}
	}
}

export const CodeBlockComponent: React.FC<
	CodeBlockComponentProps & NodeViewProps
> = ({
	node: {
		attrs: { language: selectedLanguage },
	},
	updateAttributes,
	extension,
}) => {
	const [copied, setCopied] = useState(false)
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
	const languages: string[] = extension.options.lowlight.listLanguages()
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
	const isLanguageEditable = extension.options.isLanguageEditable
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
	const preClassName = extension.options.preClassName
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
	const languageClassPrefix = extension.options.languageClassPrefix

	const wrapperRef = useRef<HTMLDivElement>(null)

	const handleCopy = () => {
		const codeContent =
			wrapperRef.current?.querySelector("code")?.textContent || ""
		navigator.clipboard
			.writeText(codeContent)
			.then(() => {
				setCopied(true)
				setTimeout(() => {
					setCopied(false)
				}, 2000)
			})
			.catch((err) => {
				console.error("Failed to copy: ", err)
			})
	}

	return (
		<NodeViewWrapper
			className="code-block group/codeblock relative w-full min-w-0"
			ref={wrapperRef}
		>
			{isLanguageEditable ? (
				<LanguageDropdown
					languages={languages}
					selectedLanguage={selectedLanguage}
					onSelect={(lang) => updateAttributes({ language: lang })}
					className="opacity-0 transition-opacity group-hover/codeblock:opacity-100"
				/>
			) : (
				selectedLanguage !== null &&
				selectedLanguage !== "plaintext" && (
					<span className="absolute right-10 top-2 flex h-7 flex-row items-center justify-center gap-1 rounded-lg bg-white/10 px-2 py-1 text-xs font-medium text-gray-200 opacity-0 transition-opacity group-hover/codeblock:opacity-100">
						{selectedLanguage}
					</span>
				)
			)}

			<button
				onClick={handleCopy}
				data-code-copy=""
				className="absolute right-2 top-2 z-10 flex cursor-pointer items-center gap-1 rounded-lg bg-white/10 px-1.5 py-1.5 text-xs font-medium text-gray-200 opacity-0 transition-opacity hover:bg-white/15 group-hover/codeblock:opacity-100"
			>
				{copied ? (
					<Check className="h-3.5 w-3.5 scale-110 transition-transform duration-200 ease-in-out" />
				) : (
					<Copy className="h-3.5 w-3.5 transition-transform duration-200 ease-in-out hover:scale-110" />
				)}
			</button>

			<pre
				className={cn(
					"w-full min-w-0 overflow-x-auto !whitespace-pre dark:!bg-gray-850",
					// Apply pt-[2.75rem] only in edit-docs context (editor)
					preClassName as string,
					(languageClassPrefix as string) + selectedLanguage,
				)}
			>
				<NodeViewContent as="code" className="flex w-max !whitespace-pre" />
			</pre>
		</NodeViewWrapper>
	)
}

interface LanguageDropdownProps {
	languages: string[]
	selectedLanguage: string | null
	onSelect: (language: string) => void
	className?: string
}

export const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
	languages,
	selectedLanguage,
	onSelect,
	className,
}) => {
	const [open, setOpen] = useState(false)

	return (
		<InternalPopover open={open} onOpenChange={setOpen}>
			<InternalPopoverTrigger asChild>
				<button
					data-code-language=""
					className={cn(
						"absolute right-10 top-2 z-10 flex flex-row items-center justify-center gap-1 rounded-lg bg-white/10 px-2 py-1 text-xs font-medium text-gray-200 hover:bg-white/15",
						className,
					)}
				>
					{selectedLanguage === "null" ? "auto" : selectedLanguage}
					<ChevronDown className="h-3 w-3" />
				</button>
			</InternalPopoverTrigger>
			{/* Outside the editor, so the menu anchors to the button and stays out of the editable code. */}
			<InternalPopoverPortal>
				<InternalPopoverContent
					className="w-48 p-0"
					side="bottom"
					align="end"
					collisionPadding={8}
					onOpenAutoFocus={(e) => e.preventDefault()}
				>
					<Command>
						<CommandInput placeholder="Search language..." autoFocus />
						{/* A fixed height keeps the menu from resizing, and so repositioning, as the search narrows. */}
						<CommandList className="h-64">
							<CommandEmpty>No language found.</CommandEmpty>
							<CommandGroup>
								{languages.map((lang) => (
									<CommandItem
										key={lang}
										value={lang}
										hint={
											lang === selectedLanguage ? (
												<Check className="h-3.5 w-3.5 text-primary" />
											) : null
										}
										onSelect={() => {
											onSelect(lang)
											setOpen(false)
										}}
										onMouseDown={(e) => {
											e.preventDefault()
											onSelect(lang)
											setOpen(false)
										}}
									>
										{lang}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</InternalPopoverContent>
			</InternalPopoverPortal>
		</InternalPopover>
	)
}
